// component
import { useSelector } from 'react-redux';
import i18 from '../../i18n';
import DashboardIcon from '../../pages/dashboard/Icon';
import InspectionsIcon from '../../pages/inspections/Icon';
import InsightsIcon from '../../pages/insights/Icon';
import ResourceIcon from '../../pages/resources/Icon';
import ItemsIcon from '../../pages/items/Icon';
import FlowsIcon from '../../pages/flows/Icon';
import UsersIcon from '../../pages/users/Icon';
import OperationModelIcon from './OperationModelIcon';
import { hasPermission } from '../../pages/login/selectors';
import { Permissions } from '../../entities/permissions';
import { Features } from '../../entities/features';
import { useIsFeatureEnabled } from '../../services/api/featuresApi';

// ----------------------------------------------------------------------

export default function NavConfig() {
  const navConfig = [];

  if (useSelector(hasPermission(Permissions.PAGE_DASHBOARD_ROOT)))
    navConfig.push({
      title: i18.t('sidebar.dashboard'),
      path: '/dashboard/app',
      icon: DashboardIcon,
    });

  if (useSelector(hasPermission(Permissions.PAGE_INSPECTIONS_ROOT))) {
    navConfig.push({
      title: i18.t('sidebar.inspections'),
      path: '/dashboard/inspections',
      icon: InspectionsIcon,
    });
  }

  // Both hooks are called unconditionally, then the result is branched on —
  // hooks cannot live inside the `if`, and this file's shape already relies on
  // that. The feature flag arrives in the authenticated features payload, not
  // from the PostHog browser SDK, so analytics consent does not gate the nav.
  const canViewInsights = useSelector(hasPermission(Permissions.PAGE_INSIGHTS_ROOT));
  const insightsEnabled = useIsFeatureEnabled(Features.FT_INSIGHTS);
  if (canViewInsights && insightsEnabled) {
    navConfig.push({
      title: i18.t('sidebar.insights'),
      path: '/dashboard/insights',
      icon: InsightsIcon,
    });
  }

  // Build Operation Model submenu children
  const operationModelChildren = [];

  if (useSelector(hasPermission(Permissions.PAGE_FLOWS_ROOT))) {
    operationModelChildren.push({
      title: i18.t('sidebar.flows'),
      path: '/dashboard/flows',
      icon: FlowsIcon,
    });
  }
  if (useSelector(hasPermission(Permissions.PAGE_ITEMS_ROOT))) {
    operationModelChildren.push({
      title: i18.t('sidebar.inspection-items'),
      path: '/dashboard/items',
      icon: ItemsIcon,
    });
  }
  if (useSelector(hasPermission(Permissions.PAGE_RESOURCES_ROOT))) {
    operationModelChildren.push({
      title: i18.t('sidebar.resources'),
      path: '/dashboard/resources',
      icon: ResourceIcon,
    });
  }
  // Tags don't get their own sidebar entry. They're managed inline from
  // any TagsMultiSelect popover via a "Manage tags" link, which deep-links
  // to /dashboard/tags for users with PAGE_TAGS_ROOT.

  // Add Operation Model menu if there are any children
  if (operationModelChildren.length > 0) {
    navConfig.push({
      title: i18.t('sidebar.operation-model'),
      icon: OperationModelIcon,
      children: operationModelChildren,
    });
  }

  const userMenuChildren = [];
  if (useSelector(hasPermission(Permissions.PAGE_USERS_ROOT))) {
    userMenuChildren.push({
      title: i18.t('sidebar.users-list'),
      path: '/dashboard/users',
    });
  }
  if (useSelector(hasPermission(Permissions.PAGE_PERMISSIONS_ROOT))) {
    userMenuChildren.push({
      title: i18.t('sidebar.permissions'),
      path: '/dashboard/permissions',
    });
  }
  if (userMenuChildren.length > 0) {
    navConfig.push({
      title: i18.t('sidebar.users'),
      icon: UsersIcon,
      children: userMenuChildren,
    });
  }

  return navConfig;
}
